import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppFlags } from 'utils/useAppFlags';
import moment from 'moment-timezone';
import React, { useEffect, useState, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { compose } from 'redux';
import { getDeviceTimeZoneIdSelector, selectSensorDeviceByIdSelector } from '@redux/device/deviceSelectors';
import {
  getPersonVitalsByPersonIdSelector,
  getSelectedPersonVitalOverview,
  getSelectedPersonDetailsSelector,
  hasPatientCreatePermissionSelector
} from '@redux/person/personSelector';
import { getPersonVitalOverviewThunk, getPersonDetailsThunk } from '@redux/person/personThunks';
import { DeviceModeState, PersonVitalOverview } from '@redux/person/personTypes';
import { getPatientEngagementPlansSelector } from '@redux/tasks/tasksSelector';
import { getPatientEngagementPlansThunk } from '@redux/tasks/tasksThunks';
import { ReduxState } from '@redux/types';
import BreadCrumbs from 'components/BreadCrumbs/BreadCrumbs';
import { Button } from 'components/Button/Button';
import DeletePatientModal from 'components/DeletePatientModal/DeletePatientModal';
import { DropDownMenu } from 'components/DropDownMenu/DropDownMenu';
import { AlarmIcon, BedIcon, HeartbeatIcon, MoonStarsIcon, WindIcon } from 'components/Icons';
import { PatientDeviceMode } from 'components/PatientDeviceMode/PatientDeviceMode';
import { Text } from 'components/Typography';
import { PatientDashboardInfoDrawer } from 'containers/PatientDashboardInfoDrawer/PatientDashboardInfoDrawer';
import { getDefaultPatientProfileBreadCrumbs, renderBreadCrumbs } from 'utils/breadcrumbs';
import { DEFAULT_TIMEZONE_ID, getElapsedTimeDisplay, formatMinutesToHoursAndMinutes } from 'utils/time';
import { getInitials, getFixedPointDisplay } from 'utils/util';
import HorizontalTabs from '../../components/HorizontalTabs/HorizontalTabs';
import NavTopBarContentWrapper from '../../components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { customDateTimePickerStyles } from '../../customStyles';
import { BasicCard } from './components/BasicCard/BasicCard';
import EngagementPlans from './components/EngagementPlans/EngagementPlans';
import Insights from './components/Insights/Insights';
import { NominalRangeCard } from './components/NominalRangeCard/NominalRangeCard';
import { VitalCard } from './components/VitalCard/VitalCard';
import style from './PatientDashboard.scss';
import { getVitalStatusOutOfRangeText, getSleepStatusOutOfRangeText } from './PatientDashboardUtil';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux;

const PatientDashboard: React.FC<Props> = ({
  getDeviceTimeZoneId,
  selectedPersonVitalOverview,
  getPersonVitalOverview,
  personVitalsByPersonIdSelector,
  patientEngagementPlans,
  getPatientEngagementPlans,
  getPersonDetails,
  selectedPersonDetails,
  sensorDeviceById,
  hasPatientCreatePermission
}) => {
  const params = useParams();
  const patientId = params?.userId ?? '';
  const navigate = useNavigate();
  const flags = useAppFlags();
  const showEngagementPlanTab = flags?.engagementPlanTab ?? false;
  const isAlzheimersStudy = flags?.alzheimersSleepStudy ?? false;

  const personName = selectedPersonVitalOverview?.personName ?? '';
  const personInitials = getInitials(personName);
  const timeZoneId = getDeviceTimeZoneId(patientId) || DEFAULT_TIMEZONE_ID;
  const [showDeletePatientConfirmation, setShowDeletePatientConfirmation] = useState(false);
  const [showViewPatient, setShowViewPatient] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(() => moment().tz(timeZoneId).toDate());
  const [vitalOverviewData, setVitalOverviewData] = useState<PersonVitalOverview | null>(null);
  const [deviceCheckLoading, setDeviceCheckLoading] = useState(true);
  const [deviceCheckError, setDeviceCheckError] = useState<string | null>(null);
  const sensorDeviceDetails = selectedPersonDetails?.sensorDeviceId ? sensorDeviceById[selectedPersonDetails.sensorDeviceId] : undefined;
  const isLivingSpace = sensorDeviceDetails?.deviceMode === DeviceModeState.LIVING_SPACE;
  const personVitals = patientId ? personVitalsByPersonIdSelector[patientId] : null;
  const { deviceStatus } = personVitals || {};
  const personPresence = personVitals?.personPresence ?? selectedPersonVitalOverview?.personPresence;
  const personPresenceLastReading = personVitals?.personPresenceLastReading ?? selectedPersonVitalOverview?.personPresenceLastReading;

  useEffect(() => {
    const fetchPersonVitalOverview = async () => {
      const result = await getPersonVitalOverview(patientId, selectedDate, timeZoneId);
      setVitalOverviewData(result);
      setInitDone(true);
    };

    fetchPersonVitalOverview();
  }, [patientId, selectedDate, timeZoneId]);

  useEffect(() => {
    const fetchPersonDetails = async () => {
      if (!patientId) return;

      try {
        setDeviceCheckLoading(true);
        setDeviceCheckError(null);
        await getPersonDetails(patientId);
      } catch (error) {
        console.error('Failed to load person details:', error);
        setDeviceCheckError('Failed to load device information. Some features may be unavailable.');
      } finally {
        setDeviceCheckLoading(false);
      }
    };

    // Load engagement plans and person details for this patient
    if (patientId) {
      getPatientEngagementPlans(patientId);
      fetchPersonDetails();
    }
  }, [patientId, getPatientEngagementPlans, getPersonDetails]);

  const onAddActivityClick = () => {
    navigate(`/patients/${patientId}/newActivity`);
  };

  const backNavigation = () => {
    navigate('/patients', { replace: true });
  };

  const onViewPatientDetailClick = () => {
    setShowViewPatient(true);
  };

  const onEditPatientClick = () => {
    navigate(`/patients/${patientId}/editPatient`);
  };

  const onDeletePatientClick = () => {
    setShowDeletePatientConfirmation(true);
  };

  // Check if patient has a device set up with robust validation
  const hasDeviceSetup = useMemo(() => {
    // If still loading or there's an error, default to allowing actions to prevent false negatives
    if (deviceCheckLoading || deviceCheckError) {
      return true;
    }

    if (!selectedPersonDetails) return false;

    if (selectedPersonDetails.deviceId?.trim()) {
      return true;
    }

    return false;
  }, [selectedPersonDetails, deviceCheckLoading, deviceCheckError]);

  const overviewContent = (
    <>
      <div className={style.overviewTextContainer}>
        <Text variant="xl">Overview</Text>
        <div style={{ paddingRight: '20px' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={selectedDate}
              onChange={(date: Date | null) => {
                if (date) {
                  setSelectedDate(date);
                }
              }}
              disableFuture={true}
              timezone={timeZoneId}
              sx={customDateTimePickerStyles}
            />
          </LocalizationProvider>
        </div>
      </div>
      <div className={style.cardsContainer}>
        {isAlzheimersStudy ? (
          <>
            <NominalRangeCard
              size="lg"
              title="Sleep Duration"
              unit="h,m"
              rawValue={selectedPersonVitalOverview?.nightSleepTimeInMinutes}
              value={
                selectedPersonVitalOverview?.nightSleepTimeInMinutes != null
                  ? formatMinutesToHoursAndMinutes(selectedPersonVitalOverview?.nightSleepTimeInMinutes, undefined, true)
                  : '-'
              }
              nominalRange={[4.22 * 60, 10.2 * 60]}
            ></NominalRangeCard>
            <NominalRangeCard
              size="lg"
              title="Awakenings Duration"
              unit="h,m"
              rawValue={selectedPersonVitalOverview?.nightAwakeTimeInMinutes}
              value={
                selectedPersonVitalOverview?.nightAwakeTimeInMinutes != null
                  ? formatMinutesToHoursAndMinutes(selectedPersonVitalOverview?.nightAwakeTimeInMinutes, undefined, true)
                  : '-'
              }
              nominalRange={[0, 103]}
            ></NominalRangeCard>
            <NominalRangeCard
              size="lg"
              title="Bed Exits"
              rawValue={selectedPersonVitalOverview?.numberOfBedExits}
              value={selectedPersonVitalOverview?.numberOfBedExits ?? '-'}
              nominalRange={[0, 4]}
            ></NominalRangeCard>
            <NominalRangeCard
              size="lg"
              title="Daytime Sleep Duration"
              unit="h,m"
              rawValue={selectedPersonVitalOverview?.daySleepTimeInMinutes}
              value={
                selectedPersonVitalOverview?.daySleepTimeInMinutes != null
                  ? formatMinutesToHoursAndMinutes(selectedPersonVitalOverview?.daySleepTimeInMinutes, undefined, true)
                  : '-'
              }
              nominalRange={[0, 45]}
            ></NominalRangeCard>
          </>
        ) : (
          <>
            <VitalCard
              vitalTitle="Heart rate"
              vitalSubtitle={!isLivingSpace && selectedPersonVitalOverview?.heartRateLastReading ? getElapsedTimeDisplay(selectedPersonVitalOverview?.heartRateLastReading) : ''}
              vitalValue={isLivingSpace ? undefined : getFixedPointDisplay(selectedPersonVitalOverview?.heartRate)}
              icon={HeartbeatIcon}
              vitalStatus={isLivingSpace ? undefined : selectedPersonVitalOverview?.heartRateStatus}
              vitalStatusText={isLivingSpace ? undefined : getVitalStatusOutOfRangeText(selectedPersonVitalOverview?.heartRateStatus)}
            />
            <VitalCard
              vitalTitle="Breathing rate"
              vitalSubtitle={!isLivingSpace && selectedPersonVitalOverview?.breathingRateLastReading ? getElapsedTimeDisplay(selectedPersonVitalOverview?.breathingRateLastReading) : ''}
              vitalValue={isLivingSpace ? undefined : getFixedPointDisplay(selectedPersonVitalOverview?.breathingRate)}
              icon={WindIcon}
              vitalStatus={isLivingSpace ? undefined : selectedPersonVitalOverview?.breathingRateStatus}
              vitalStatusText={isLivingSpace ? undefined : getVitalStatusOutOfRangeText(selectedPersonVitalOverview?.breathingRateStatus)}
            />
            <VitalCard
              vitalTitle="Total sleep"
              showVitalSubtitleStub
              icon={MoonStarsIcon}
              vitalUnit="h,m"
              vitalValue={isLivingSpace ? undefined : formatMinutesToHoursAndMinutes(selectedPersonVitalOverview?.sleepTimeInMinutes ?? 0)}
              vitalStatus={isLivingSpace ? undefined : selectedPersonVitalOverview?.sleepStatus}
              vitalStatusText={isLivingSpace ? undefined : getSleepStatusOutOfRangeText(selectedPersonVitalOverview?.sleepStatus)}
            />
            <BasicCard size="lg" title="Bed Exits" icon={() => <AlarmIcon width={32} height={32} />} value={isLivingSpace ? '-' : (selectedPersonVitalOverview?.numberOfBedExits ?? '-')} />
            <BasicCard
              size="lg"
              title="Total time in bed"
              icon={() => <BedIcon width={32} height={32} />}
              value={isLivingSpace ? '-' : formatMinutesToHoursAndMinutes(selectedPersonVitalOverview?.totalTimeInBedInMinutes ?? 0)}
            />
          </>
        )}
      </div>
    </>
  );

  let tabs = [
    { label: 'Overview', content: overviewContent },
    { label: 'Insights', content: <Insights personId={patientId} timeZoneId={timeZoneId} /> }
  ];

  const handleRefreshEngagementPlans = () => {
    if (patientId) {
      getPatientEngagementPlans(patientId);
    }
  };

  if (showEngagementPlanTab) {
    tabs.push({
      label: 'Engagement',
      content: (
        <EngagementPlans
          patientId={patientId}
          patientEngagementPlans={patientEngagementPlans}
          onRefreshEngagementPlans={handleRefreshEngagementPlans}
          hasDeviceSetup={hasDeviceSetup}
        />
      )
    });
  }

  const defaultCrumbHistory = getDefaultPatientProfileBreadCrumbs(personName);

  const actionsMenuItems = [
    {
      text: 'View patient details',
      onClick: onViewPatientDetailClick
    },
    ...(hasPatientCreatePermission
      ? [
          {
            text: 'Edit',
            onClick: onEditPatientClick
          }
        ]
      : []),
    ...(hasPatientCreatePermission
      ? [
          {
            text: 'Deactivate',
            textClassName: style.redText,
            onClick: onDeletePatientClick
          }
        ]
      : [])
  ];

  return (
    <NavTopBarContentWrapper>
      {initDone && (
        <div className={style.mainContainer}>
          {/* Patient Details and Activity */}
          <div className={style.patientDetailSection}>
            <BreadCrumbs aria-label="breadcrumb">{renderBreadCrumbs(defaultCrumbHistory)}</BreadCrumbs>
            <div className={style.avatarDeviceContainer}>
              <div className={style.avatarAndText}>
                <div className={style.avatar}>
                  <Text variant="xl" className={style.personInitials}>
                    {personInitials}
                  </Text>
                </div>
                <Text variant="2xl">{selectedPersonVitalOverview?.personName}</Text>
              </div>
              <PatientDeviceMode
                deviceMode={sensorDeviceDetails?.deviceMode}
                personPresence={personPresence}
                lastReadingTime={personPresenceLastReading}
                deviceStatus={deviceStatus}
                containerStyle={style.patientPresenceContainer}
              />
            </div>

            <div className={style.actionsContainer}>
              <Tooltip title="Coming soon" placement="top">
                <div>
                  <Button disabled variant="contained" color="primary" className={style.addButton} onClick={onAddActivityClick} startIcon={<AddIcon />}>
                    Add Activity
                  </Button>
                </div>
              </Tooltip>

              <DropDownMenu menuText="Actions" additionalButtonClass={style.dropDownButton} menuItems={actionsMenuItems} disabled={actionsMenuItems.length === 0} />
            </div>
            <DeletePatientModal
              patientId={patientId}
              visible={showDeletePatientConfirmation}
              onClosePress={() => setShowDeletePatientConfirmation(false)}
              onConfirmPress={backNavigation}
            />
            <PatientDashboardInfoDrawer personId={patientId} showDialog={showViewPatient} onDialogClose={() => setShowViewPatient(false)} />
          </div>
          <HorizontalTabs tabs={tabs} buttonClassName={style.HorizontalTabsButton} tabsContentClassname={style.tabsContent} />
        </div>
      )}
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    getDeviceTimeZoneId: (personId: string) => getDeviceTimeZoneIdSelector(personId, state),
    selectedPersonVitalOverview: getSelectedPersonVitalOverview(state),
    personVitalsByPersonIdSelector: getPersonVitalsByPersonIdSelector(state),
    patientEngagementPlans: getPatientEngagementPlansSelector(state),
    selectedPersonDetails: getSelectedPersonDetailsSelector(state),
    hasPatientCreatePermission: hasPatientCreatePermissionSelector(state),
    sensorDeviceById: selectSensorDeviceByIdSelector(state)
  }),
  {
    getPersonVitalOverview: getPersonVitalOverviewThunk,
    getPatientEngagementPlans: getPatientEngagementPlansThunk,
    getPersonDetails: getPersonDetailsThunk
  }
);

export default compose(connectRedux)(PatientDashboard);
